import React, { Fragment, useEffect, useState } from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";

import EntityComponent from "../../common/entity-component";
import Snackbar from "../../common/Snackbar";

import config from "./config";
import PlanForm from "./form";
import { Plan } from "../../../types/Plan";

//@ts-ignore
import getAllAuthors from "../../../database/authors/getAllAuthors";

import getAllCategories from "../../../database/plans/getAllCategories";
import getAllPlans, {
  PlansResponse,
} from "../../../database/plans/getAllPlans";
import removePlan from "../../../database/plans/removePlan";
import createPlan from "../../../database/plans/updatePlan";
import StoreUser from "../../../types/StoreUser";
import createDailyStep from "../../../database/dailyStep/createDailyStep";
import createDayPlan from "../../../database/dayPlan/createDayPlan";
import { firestore } from "../../../database/firebase";
import DB_PATH from "../../../database/DB_PATH";
import { updateDailyStep } from "../../../database/dailyStep/updateDailyStep";
import { updateDayPlan } from "../../../database/dayPlan/updateDayPlan";
import updatePlan from "../../../database/plans/updatePlan";
import ImportPlans from "./components/ImportPlans";
import PlanStepsTab from "./components/PlanStepsTab";
import LessonExperimentsTab from "./components/LessonExperimentsTab";
import ArtifactTab from "../analytics/ArtifactTab";
import ExportExcel from "../customers/components/storeCustomers/components/ExportExcel";

import {
  DesignSystemSurface,
  PageHeader,
} from "../../../design-system";
import { IconBook, IconSparkles } from "../../../design-system/icons/icons";

const Page = styled.div`
  min-height: calc(100vh - 104px);
  padding: var(--space-6) var(--space-6) var(--space-16);
`;

const PageInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const TabBar = styled.div`
  display: inline-flex;
  gap: 2px;
  padding: 4px;
  background: var(--color-surface-subtle);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  margin-bottom: var(--space-6);
`;

const TabButton = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-4);
  border: none;
  background: transparent;
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-secondary);
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition:
    background var(--duration-fast) var(--ease-standard),
    color var(--duration-fast) var(--ease-standard);

  &:hover {
    color: var(--color-text);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }

  ${(p) =>
    p.$active &&
    css`
      background: var(--color-surface);
      color: var(--color-text);
      font-weight: var(--font-weight-semibold);
      box-shadow: var(--shadow-xs);
    `}
`;

type Props = {
  plan: Plan;
  user: StoreUser;
};

const PlanScreen = ({ user }: Props) => {
  const [currentTabIndex, setTabIndex] = useState(0);

  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const [plansRefreshKey, setPlansRefreshKey] = useState(0);
  const planResponse: PlansResponse = getAllPlans({
    communityId: user?.communityId,
    refreshKey: plansRefreshKey,
  });
  const authorsResponse = getAllAuthors({ communityId: user?.communityId });
  const categoriesResponse = getAllCategories({
    communityId: user?.communityId,
  });

  useEffect(() => {
    const { message } = planResponse;
    if (message) {
      setIsSnackbarOpen(true);
    }
  }, [planResponse]);

  const onCreatePlan = async (plan: Plan) => {
    const newPlanRef = firestore.collection(DB_PATH.PLAN).doc();
    const newPlanId = newPlanRef.id;
    plan.id = newPlanId;

    plan.dayPlans.map((dayPlan) => {
      const dayPlanId =
        dayPlan.id && dayPlan.id.trim().length > 0
          ? dayPlan.id
          : firestore.collection(DB_PATH.PLAN_DAY).doc().id;
      dayPlan.id = dayPlanId;

      dayPlan.steps.map((step) => {
        createDailyStep({
          dailyStep: {
            ...step,
            authorID: plan.author?.id,
            authorName: plan.author?.fullName,
            dayPlanID: dayPlanId,
            planID: plan.id,
            communityId: user?.communityId,
          },
        });
      }, {});
      delete dayPlan.steps;
      createDayPlan({
        dayPlan: {
          ...dayPlan,
          planID: plan.id,
          communityId: user?.communityId,
        },
      });
    });

    delete plan.dayPlans;
    delete plan.author;
    createPlan({ plan: { ...plan, communityId: user?.communityId } });
  };

  const onRemovePlan = ({ id }) => {
    removePlan({ planID: id });
  };

  const onEditPlan = async (plan: Plan) => {
    if (plan.author) {
      plan.authorName = plan.author?.fullName || "";
      plan.authorId = plan.author?.id || "";
    }

    try {
      for (const dayPlan of plan.dayPlans || []) {
        const hasExistingDayPlanId =
          !!dayPlan.id && dayPlan.id.trim().length > 0;
        const dayPlanId = hasExistingDayPlanId
          ? dayPlan.id
          : firestore.collection(DB_PATH.PLAN_DAY).doc().id;
        dayPlan.id = dayPlanId;

        for (const step of dayPlan.steps || []) {
          if (step.id) {
            await updateDailyStep({
              dailyStep: {
                ...step,
                authorID: plan.authorId,
                authorName: plan.authorName,
                dayPlanID: dayPlanId,
                planID: plan.id,
                communityId: user?.communityId,
              },
            });
          } else {
            await createDailyStep({
              dailyStep: {
                ...step,
                authorID: plan.authorId,
                authorName: plan.authorName,
                dayPlanID: dayPlanId,
                planID: plan.id,
                communityId: user?.communityId,
              },
            });
          }
        }
        delete dayPlan.steps;

        if (hasExistingDayPlanId) {
          await updateDayPlan({
            dayPlan: {
              ...dayPlan,
              planID: plan.id,
              communityId: user?.communityId,
            },
          });
        } else {
          const newDayPlanId = firestore.collection(DB_PATH.PLAN_DAY).doc().id;
          dayPlan.id = newDayPlanId;
          await createDayPlan({
            dayPlan: {
              ...dayPlan,
              id: newDayPlanId,
              planID: plan.id,
              communityId: user?.communityId,
            },
          });
        }
      }
    } catch (error) {
      console.error("onEditPlan: dayPlan/step write failed, continuing with plan update", error);
    }

    delete plan.dayPlans;
    delete plan.author;

    await updatePlan({ plan: { ...plan, communityId: user?.communityId } });
  };

  const renderSnackbar = (response: any): React.ReactNode => {
    const { message, error } = response;
    return (
      <Snackbar
        onCloseSnackbar={() => setIsSnackbarOpen(false)}
        isOpen={isSnackbarOpen}
        message={message}
        error={error}
      />
    );
  };

  const TABS: { id: number; label: string; icon?: React.ReactNode }[] = [
    { id: 0, label: "Plans", icon: <IconBook size={14} /> },
    { id: 1, label: "Steps", icon: <IconSparkles size={14} /> },
    { id: 2, label: "Learning health", icon: <IconSparkles size={14} /> },
    { id: 3, label: "Artifacts", icon: <IconSparkles size={14} /> },
  ];

  const renderPlansFragment = () => (
    <Fragment>
      <EntityComponent
        canBeCreated
        canBeRemoved
        canBeEdited
        onRemoveItem={onRemovePlan}
        onEditItem={onEditPlan}
        onCreateItem={onCreatePlan}
        singularEntityName="Plan"
        pluralEntityName="Plans"
        filterConfig={config.filterConfig}
        tabConfig={config.tabConfig}
        dataset={planResponse.data.combined}
        ExtraComponent={(values) => (
          <div>
            <ImportPlans
              communityId={user?.communityId}
              authors={authorsResponse}
              onImported={() => setPlansRefreshKey((k) => k + 1)}
            />
            <ExportExcel values={planResponse.data.plans} title="Plans" />
          </div>
        )}
        Form={(props) => (
          <PlanForm
            categories={categoriesResponse}
            authors={authorsResponse}
            plans={planResponse.data.plans}
            communityId={user?.communityId}
            {...props}
          />
        )}
      />
      {renderSnackbar(planResponse)}
    </Fragment>
  );

  const renderStepsFragment = () => (
    <PlanStepsTab
      authors={authorsResponse}
      loading={planResponse.loading}
      plans={planResponse.data.plans}
      user={user}
    />
  );

  return (
    <DesignSystemSurface>
      <Page>
        <PageInner>
          <PageHeader
            title="Plans"
            subtitle="Multi-day programs your community follows."
          />
          <TabBar role="tablist">
            {TABS.map((tab) => (
              <TabButton
                key={tab.id}
                type="button"
                role="tab"
                $active={currentTabIndex === tab.id}
                aria-selected={currentTabIndex === tab.id}
                onClick={() => setTabIndex(tab.id)}
              >
                {tab.icon}
                {tab.label}
              </TabButton>
            ))}
          </TabBar>
          {currentTabIndex === 0 && renderPlansFragment()}
          {currentTabIndex === 1 && renderStepsFragment()}
          {currentTabIndex === 2 && (
            <LessonExperimentsTab communityId={user?.communityId} />
          )}
          {currentTabIndex === 3 && (
            <ArtifactTab communityId={user?.communityId} days={30} />
          )}
        </PageInner>
      </Page>
    </DesignSystemSurface>
  );
};

const mapStateToProps = (state) => ({
  store: state.store,
  user: state.storeUser,
});

export default connect(mapStateToProps, null)(PlanScreen);
